import React, { useCallback, useMemo } from 'react';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  sensorDevicesSelectors,
  useGetHomeQuery,
} from '../../app/services/api';
import { CompanyStatItem } from '../../components/CompanyStatItem';
import { sensorStatusColors } from '../../configs/sensorStatusColors';
import { sensorStatusLabels } from '../../configs/sensorStatusLables';
import { mergeDisplaySettings } from '../../types/DisplaySettings';
import { SensorStatus, SensorStatusWithAll } from '../../types/SensorStatus';
import { companySelectors, companySlice } from './companySlice';

const initStatKeys: SensorStatusWithAll[] = [
  SensorStatusWithAll.all,
  SensorStatusWithAll.IN,
  SensorStatusWithAll.OUT,
  SensorStatusWithAll.MEETING,
];
const statLabels: Record<SensorStatusWithAll, string> = {
  ...sensorStatusLabels,
  all: '전체',
};
const initStats: Record<SensorStatusWithAll, number> = {
  all: 0,
  MEETING: 0,
  IN: 0,
  OUT: 0,
};
export function CompanyStats() {
  const dispatch = useAppDispatch();
  const { filterSensorStatus } = useAppSelector(companySelectors.selectState);
  const { data } = useGetHomeQuery();
  const displaySettings = useMemo(
    () => mergeDisplaySettings(data?.displaySettings),
    [data?.displaySettings],
  );
  const [statKeys, stats] = useMemo(() => {
    if (!data) {
      return [initStatKeys, { ...initStats }];
    }
    // Only the core statuses (전체/재실/부재중/회의중) appear in the top legend;
    // custom ("기타") statuses are intentionally excluded to keep it simple.
    const keys = initStatKeys;
    const sensorDevices = sensorDevicesSelectors.selectAll(data);

    return [
      keys,
      sensorDevices.reduce(
        (acc, cur) => {
          if (cur.status) {
            if (acc[cur.status] === undefined) {
              acc[cur.status] = 0;
            }
            acc[cur.status] += 1;
          }
          acc.all += 1;
          return acc;
        },
        { ...initStats } as Record<SensorStatusWithAll | string, number>,
      ),
    ];
  }, [data]);

  const handleClickFactory = useCallback(
    (key: SensorStatusWithAll | string) => () => {
      dispatch(companySlice.actions.setFilterSensorStatus(key));
    },
    [dispatch],
  );
  return (
    <Stack
      direction="row"
      divider={<Divider orientation="vertical" flexItem />}
      spacing={2.5}
      maxWidth={'100%'}
      useFlexGap
      flexWrap="wrap"
    >
      {stats
        ? statKeys.map((key, i) => (
            <CompanyStatItem
              key={key}
              isSelected={filterSensorStatus === key}
              onClick={handleClickFactory(key)}
              sx={{
                fontSize: `${displaySettings.statFontSize}px`,
                fontWeight: 700,
              }}
            >
              {sensorStatusColors[key as SensorStatus] ? (
                <span
                  style={{
                    display: 'inline-block',
                    width: '0.8em',
                    height: '0.8em',
                    borderRadius: '50%',
                    marginRight: '0.45em',
                    verticalAlign: 'middle',
                    // Mirror the configurable card background color so the
                    // legend dot stays in sync when an admin changes a status
                    // color. A faint border keeps very light colors visible.
                    backgroundColor:
                      key === SensorStatus.IN
                        ? displaySettings.presentBackgroundColor
                        : key === SensorStatus.OUT
                        ? displaySettings.absentBackgroundColor
                        : displaySettings.meetingBackgroundColor,
                    border: '1px solid rgba(0,0,0,0.12)',
                    boxSizing: 'border-box',
                  }}
                />
              ) : null}
              <span>{statLabels[key as SensorStatusWithAll] || key + ' '}</span>{' '}
              <span style={{ fontWeight: 'bold' }}>{stats[key] || '0'}</span>
              <span>명</span>
            </CompanyStatItem>
          ))
        : null}
    </Stack>
  );
}
